import { useEffect, useState } from "react";
import { Smartphone, Share, PlusSquare, Chrome, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import TripAutoHero from "../components/TripAutoHero";
import InstallQrCard from "../components/InstallQrCard";

/* /download — marketing landing that explains "Install → Add to Home Screen"
   for both TravelerPulse and My Social Pulse (the /app shell). No app-store
   dependencies — this is a PWA install guide. */

function detectPlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

export default function Download() {
  const [platform, setPlatform] = useState("desktop");
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    document.title = "Install TravelerPulse & My Social Pulse | Download";
    setPlatform(detectPlatform());
    const listener = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", listener);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", listener);
  }, []);

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="download-page">
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="phone airport traveler mobile app" alt="Install TravelerPulse on your phone" className="absolute inset-0 w-full h-full" testid="download-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/85 via-blue-800/70 to-emerald-600/55" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-white/85">Install · Progressive Web App</div>
          <h1 className="font-serif font-800 text-4xl sm:text-5xl lg:text-6xl text-white mt-2 leading-tight">
            TravelerPulse &amp; My Social Pulse<br/>on your phone in seconds
          </h1>
          <p className="text-white/90 mt-4 max-w-2xl text-lg">
            Both apps are Progressive Web Apps — no App Store, no download bar.
            One tap to add them to your home screen and they work offline for
            your saved trips, boarding passes and travel docs.
          </p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">

        {/* Scan-from-laptop QR shortcut — visible on every device */}
        <InstallQrCard variant="full" />

        {/* Direct install button (Android + desktop Chrome) */}
        {deferredPrompt && !installed && (
          <button onClick={triggerInstall} data-testid="pwa-install-btn"
            className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-800 text-base px-6 py-3.5 rounded-full shadow-lg">
            <Smartphone className="w-5 h-5" /> Install this app now
          </button>
        )}
        {installed && (
          <div className="mt-8 inline-flex items-center gap-2 bg-emerald-50 text-emerald-800 border border-emerald-300 px-5 py-2.5 rounded-full font-800 text-sm">
            <CheckCircle2 className="w-5 h-5" /> Installed — find it on your home screen
          </div>
        )}

        {/* Two-app grid */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <AppCard title="TravelerPulse" tag="Your paid travel workspace"
            path="/travel-hub" testid="download-card-tp"
            bullets={[
              "Every trip, plan, ticket, boarding pass & receipt in one hub",
              "Inner Circle sharing with granular permissions",
              "Offline packs so it works on flights & in remote areas",
              "Guardian mode + SOS for family peace of mind",
            ]} />
          <AppCard title="My Social Pulse" tag="Part of the SocialPulse Network"
            path="/app" testid="download-card-msp"
            bullets={[
              "Native app shell — feels like an iOS/Android app",
              "One-tap access to Flights, Hotels, Expenses & Documents",
              "Personalise your dashboard cards",
              "Free forever — powered by the same TravelerPulse backend",
            ]}
            gradient="from-indigo-700 via-fuchsia-700 to-rose-600" />
        </div>

        {/* Per-platform install instructions */}
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          <Step icon={<Smartphone className="w-5 h-5" />} title="iOS · Safari"
            highlight={platform === "ios"} testid="steps-ios"
            steps={[
              <>Open this page in <b>Safari</b> (not Chrome).</>,
              <>Tap the <b>Share</b> button <Share className="inline w-3.5 h-3.5 -translate-y-0.5" /> at the bottom.</>,
              <>Scroll and tap <b>Add to Home Screen</b> <PlusSquare className="inline w-3.5 h-3.5 -translate-y-0.5" />.</>,
              <>Tap <b>Add</b> — a real app icon appears on your home screen.</>,
            ]} />
          <Step icon={<Smartphone className="w-5 h-5" />} title="Android · Chrome"
            highlight={platform === "android"} testid="steps-android"
            steps={[
              <>Open this page in <b>Chrome</b>.</>,
              <>Tap the <b>⋮ menu</b> in the top-right corner.</>,
              <>Tap <b>Install app</b> (or "Add to Home screen").</>,
              <>Confirm — you'll get a native-feeling icon on your home screen.</>,
            ]} />
          <Step icon={<Chrome className="w-5 h-5" />} title="Desktop · Chrome / Edge"
            highlight={platform === "desktop"} testid="steps-desktop"
            steps={[
              <>Look for the <b>install icon</b> at the right edge of the address bar.</>,
              <>Or open the <b>⋮ menu</b> → <b>Install TravelerPulse</b>.</>,
              <>The app opens in its own window with no browser chrome.</>,
              <>Pin it to your taskbar or dock.</>,
            ]} />
        </div>

        <div className="mt-14 text-sm text-slate-500 max-w-2xl">
          Questions? <Link to="/support" className="underline hover:text-slate-900">Contact support</Link>.
          Both apps are free to install; TravelerPulse membership tiers unlock the paid workspace features.
        </div>
      </div>
    </div>
  );
}

function AppCard({ title, tag, path, bullets, gradient = "from-blue-700 via-cyan-600 to-emerald-500", testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-lg transition-shadow" data-testid={testid}>
      <div className={`bg-gradient-to-br ${gradient} text-white p-5`}>
        <div className="text-[10px] font-800 uppercase tracking-widest text-white/85">{tag}</div>
        <div className="font-serif font-800 text-2xl mt-1">{title}</div>
      </div>
      <div className="p-5">
        <ul className="space-y-2 text-sm text-slate-700">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {b}
            </li>
          ))}
        </ul>
        <Link to={path} data-testid={`${testid}-open`}
          className="mt-4 inline-flex items-center gap-1.5 font-800 text-blue-700 hover:text-blue-900 text-sm">
          Open {title} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Step({ icon, title, steps, highlight, testid }) {
  return (
    <div data-testid={testid}
      className={`rounded-2xl border p-5 ${highlight ? "border-blue-500 bg-blue-50/40 shadow-md" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2 text-slate-900 font-800">
        {icon} {title}
        {highlight && <span className="ml-auto text-[10px] font-800 uppercase tracking-widest text-blue-700">You&apos;re here</span>}
      </div>
      <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal pl-4">
        {steps.map((s, i) => <li key={i} className="pl-1">{s}</li>)}
      </ol>
    </div>
  );
}
